import { useEffect, useRef } from 'react';
import type { HandOffConversation } from '../../types/handoff';
import { useHandoffMessagesQuery } from '../../hooks/useHandoff';
import { useMyAgentQuery } from '../../hooks/useAgents';
import { HandoffMessageList } from './HandoffMessageList';
import { AgentReplyComposer } from './AgentReplyComposer';

type Props = {
  conversation: HandOffConversation | null;
};

export function HandoffConversationPanel({ conversation }: Props) {
  const conversationId = conversation?.id ?? null;
  const { data: messages = [], isLoading } = useHandoffMessagesQuery(conversationId);
  const { data: me } = useMyAgentQuery();
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages.length, conversationId]);

  if (!conversation) {
    return (
      <section className="flex min-w-0 items-center justify-center bg-white">
        <div className="max-w-sm text-center">
          <h2 className="text-lg font-semibold text-gray-950">Välj ett ärende</h2>
          <p className="mt-2 text-sm text-gray-500">
            Välj en chatt i listan för att se historik, ta över och svara kunden.
          </p>
        </div>
      </section>
    );
  }

  return (
    <section className="flex min-h-0 min-w-0 flex-col bg-white">
      <header className="border-b px-5 py-4">
        <div className="flex items-center justify-between gap-4">
          <div className="min-w-0">
            <h2 className="truncate text-lg font-semibold text-gray-950">
              {conversation.customerName || 'Okänd kund'}
            </h2>
            <p className="mt-1 text-sm text-gray-500">
              {conversation.channel || 'webchat'} · {conversation.handoffStatus}
            </p>
            <p className="mt-0.5 break-all font-mono text-[11px] text-gray-400" title="Session ID">
              {conversation.id}
            </p>
            {me && conversation.handoffStatus !== 'resolved' && (
              <p className="mt-0.5 text-xs text-blue-600">du svarar som {me.alias}</p>
            )}
          </div>
        </div>
      </header>

      <div ref={scrollRef} className="min-h-0 flex-1 overflow-y-auto bg-gray-50 px-5 py-4">
        {isLoading ? (
          <div className="text-sm text-gray-500">Laddar meddelanden...</div>
        ) : (
          <HandoffMessageList messages={messages} />
        )}
      </div>

      {conversation.handoffStatus === 'resolved' ? (
        <div className="border-t bg-gray-50 px-5 py-4 text-center text-sm text-gray-500">
          Ärendet är avslutat – endast läsning.
        </div>
      ) : (
        <AgentReplyComposer conversation={conversation} />
      )}
    </section>
  );
}
